"use client";

import { useEffect, useState } from "react";
import { Archive, ArchiveRestore, Clock, Layers, Lock, Search, Zap } from "lucide-react";

/* Measured, not typed. The per-shelf speed on this card used to be the words "milliseconds" and
   "slower, still searchable" — unfalsifiable, and quietly wrong the moment a tier moves hardware.
   This reads the running system through the same server-side rewrite the other live figures on this
   page use (/api/index/tiers -> search_index /live/tiers), which times a real request to each shelf.

   NO FALLBACK LITERAL, deliberately, matching StorageEfficiencyLive: a number that could not be
   verified must not be printed at all. Until it arrives each shelf shows a dash. */
const ENDPOINT = "/api/index/tiers";

type TierLive = { tier?: string; reachable?: boolean; latency_ms?: number | null };

/**
 * StorageTiersDiagram — why holding the whole web stays affordable.
 *
 * Sits under "the economics" because it is the other half of that argument. The section above says a
 * page costs 7.5 KB; this says not every page needs to sit on the same shelf. Cheap-per-page and
 * tiered-by-age are the two things that together make reading the web an ordinary cost.
 *
 * WHAT IT SHOWS AND WHY IT ANIMATES. The claim is about DEPTH — how far back a search has to reach —
 * and depth is a sequence, not a quantity. A static four-box picture would say "we have four tiers",
 * which is an inventory. Watching four different searches stop at different depths says the thing
 * that actually matters: almost everything is answered by the first shelf, and the deeper ones exist
 * so that the rare question that needs them still gets an answer instead of a shrug.
 *
 * NO VENDOR NAMES, deliberately, matching every other diagram on the marketing site. Which machines
 * and which providers hold which tier is an operational detail that changes; the shape of the
 * argument does not. It is also the kind of detail that dates a page badly.
 *
 * Reduced motion is honoured by jumping to the deepest example and holding it — that frame carries
 * the whole point, so a reader who has asked for stillness loses nothing but the sequence.
 */

type Tier = {
  key: string;          // matches the `tier` name reported by /live/tiers
  name: string;
  holds: string;
  cost: string;         // what the shelf is FOR — this is the part that differs, not the speed
  note: string;
  Icon: typeof Zap;
};

const TIERS: Tier[] = [
  {
    key: "hot",
    name: "Immediate",
    holds: "The newest of everything",
    cost: "fastest storage, smallest",
    note: "Answers almost every search on its own.",
    Icon: Zap,
  },
  {
    key: "warm",
    name: "Recent",
    holds: "The months behind that",
    cost: "larger, cheaper per page",
    note: "Reached when a question looks further back than the newest material.",
    Icon: Clock,
  },
  {
    key: "cold",
    name: "Deep",
    holds: "Years of older material",
    cost: "largest, cheapest per page",
    note: "Rarely needed — and the reason a thorough question still gets a real answer.",
    Icon: Layers,
  },
  {
    key: "thaw",
    name: "Recalled",
    holds: "Material put away, brought back",
    cost: "fetched on request, not kept ready",
    note: "Not sitting on a shelf at all — retrieved when a question genuinely reaches for it.",
    Icon: ArchiveRestore,
  },
];

/* Examples chosen so the depths are obviously different questions, not four ways of asking one.
   The fourth has to be a question no amount of recent material can answer, or the last shelf
   never lights up and the picture becomes the inventory this file exists to avoid. */
const QUERIES = [
  { q: "filings published today", depth: 1 },
  { q: "how this changed last quarter", depth: 2 },
  { q: "every ruling since 2019", depth: 3 },
  { q: "the original wording, before it was amended", depth: 4 },
];

const HOLD = 2; // beats to hold a finished search before the next one starts

export function StorageTiersDiagram(
  { initial }: { initial?: Record<string, TierLive> | null },
) {
  const [step, setStep] = useState(0);
  const [qi, setQi] = useState(0);
  /* Seeded by the server so the figure is in the HTML on first paint. Before this the value
     arrived only after hydration, so every visitor saw a dash first and anything that does not
     run JavaScript — crawlers, link previews — never saw it at all. The client fetch below is
     kept: it refreshes a page that may have been cached for minutes. */
  const [live, setLive] = useState<Record<string, TierLive> | null>(initial ?? null);

  useEffect(() => {
    let alive = true;
    fetch(ENDPOINT, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { tiers?: TierLive[] }) => {
        if (!alive) return;
        const by: Record<string, TierLive> = {};
        for (const t of d?.tiers ?? []) if (t?.tier) by[t.tier] = t;
        setLive(by);
      })
      .catch(() => { /* leave the dash in place; see the note on ENDPOINT */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setQi(2);
      setStep(TIERS.length);
      return;
    }
    let s = 0;
    let q = 0;
    const t = setInterval(() => {
      s += 1;
      if (s > QUERIES[q].depth + HOLD) {
        s = 0;
        q = (q + 1) % QUERIES.length;
        setQi(q);
      }
      setStep(s);
    }, 800);
    return () => clearInterval(t);
  }, []);

  const active = QUERIES[qi];
  const answered = step > active.depth;

  return (
    <div className="relative overflow-hidden rounded-2xl border hairline-strong bg-card">
      <div className="flex items-center justify-between border-b hairline px-6 py-4">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-foreground/50">
          Where the index lives
        </span>
        <span className="hidden font-mono text-xs text-foreground/40 sm:inline">
          four shelves · searchable to the last one
        </span>
      </div>

      <div className="p-6 md:p-8">
        {/* The search being answered right now. */}
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="inline-flex items-center gap-2 rounded-full border hairline-strong bg-background px-3.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-foreground/45" aria-hidden="true" />
            <span className="font-mono text-xs text-foreground/75">{active.q}</span>
          </span>
          <span
            className={[
              "font-mono text-[11px] transition-opacity duration-500",
              answered ? "opacity-100" : "opacity-0",
            ].join(" ")}
          >
            <span className="text-foreground/45">answered from </span>
            <span className="font-semibold text-[hsl(var(--accent))]">
              {TIERS.slice(0, active.depth).map((t) => t.name).join(" + ")}
            </span>
          </span>
        </div>

        {/* Shelves. A tier lights up as the search reaches it and stays lit for that pass. */}
        <div className="mt-6 space-y-3">
          {TIERS.map((t, i) => {
            const reached = step > i;
            const isDeepest = i === active.depth - 1;
            return (
              <div
                key={t.key}
                className={[
                  "rounded-xl border p-4 transition-all duration-500 md:p-5",
                  reached
                    ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5"
                    : "hairline bg-background",
                ].join(" ")}
              >
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <div className="flex items-center gap-2.5">
                    <t.Icon
                      className={[
                        "h-4 w-4 shrink-0 transition-colors duration-500",
                        reached ? "text-[hsl(var(--accent))]" : "text-foreground/35",
                      ].join(" ")}
                      aria-hidden="true"
                    />
                    <span className="text-sm font-semibold">{t.name}</span>
                    <span className="text-xs text-foreground/55">{t.holds}</span>
                  </div>
                  <span className="flex items-baseline gap-2 font-mono text-[11px]">
                    {/* Measured. A dash until the figure arrives — never a placeholder number. */}
                    <span className="text-foreground/70">
                      {typeof live?.[t.key]?.latency_ms === "number"
                        ? `${live[t.key].latency_ms} ms`
                        : "—"}
                    </span>
                    <span className="text-foreground/40">{t.cost}</span>
                  </span>
                </div>

                {/* Fill bar: the search sweeping this shelf. */}
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className={[
                      "h-full rounded-full transition-all duration-700",
                      reached ? "bg-[hsl(var(--accent))]" : "bg-transparent",
                    ].join(" ")}
                    style={{ width: reached ? "100%" : "0%" }}
                  />
                </div>

                <p
                  className={[
                    "mt-2 text-[11px] leading-relaxed transition-colors duration-500",
                    isDeepest && answered ? "text-foreground/70" : "text-foreground/45",
                  ].join(" ")}
                >
                  {t.note}
                </p>
              </div>
            );
          })}
        </div>

        {/* The copy that is never searched, and should look different because of it. */}
        <div className="mt-3 rounded-xl border border-dashed hairline bg-background p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex items-center gap-2.5">
              <Archive className="h-4 w-4 shrink-0 text-foreground/35" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground/70">Sealed copy</span>
              <span className="text-xs text-foreground/50">A second copy of everything</span>
            </div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-foreground/45">
              <Lock className="h-3 w-3" aria-hidden="true" />
              never on the search path
            </span>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-foreground/45">
            Held elsewhere, encrypted before it leaves us, and read only if something has to be
            rebuilt. It costs almost nothing precisely because nothing asks it questions.
          </p>
        </div>
      </div>

      <div className="border-t hairline px-6 py-4">
        <p className="text-[11px] leading-relaxed text-foreground/45">
          Not every page deserves the same shelf. The newest material sits where answers are
          instant, and older material moves to storage that is larger and cheaper per page without
          ever leaving the index — so a question about last week and a question about five years ago
          are both answerable, and neither is priced as though it were the other. Older still can be
          put away entirely and brought back when something asks for it.{" "}
          <span className="text-foreground/35">
            Times shown are measured live: the median of three timed requests from the search service
            to each shelf, refreshed every few minutes. They are the cost of reaching that shelf, not
            of a whole search.
          </span>
        </p>
      </div>
    </div>
  );
}
